import React, { Fragment, useState, useEffect } from "react";
import { sortBy, keyBy, cloneDeep } from "lodash";
import { Formik, Form, FieldArray } from "formik";
import { Alert, AlertVariant } from "../../../components/src/Alert";
import stableStringify from "json-stable-stringify";
import { gql, useQuery, useMutation } from "urql";
import { useParams, Link, useLocation } from "wouter";

import { Button } from "../../../components/src/Button";
import { GET_TRANSACTION, CREATE_TRANSACTION, UPDATE_TRANSACTIONS } from "../../../graphql/src/transaction/queries";
import { emptyObjectId } from "../../../common/src";
import { recursivelyRemoveKey } from "../../../common/src/utils";
import { formikUrqlErrorFormater } from "../../../components/src/common/formik-urql-error-helper";
import {
    SelectField,
    TextField,
    NumberField,
    DateField,
    SubmitButton,
    SearchListField,
    NullableDateField,
    TimestampField
} from "../../../components/src/form";

import { YesNoModal } from "../../../components/src/YesNoModal";
import { useAlertTimeOut, usePrevious } from "../../../components/src/common/Utils";
import {
    Transaction,
    SourceType,
    BestExecutionType,
    TransactionStatus,
    TransactionType,
    TransactionItem,
    CreateTransactionItemInput,
    TransactionItemType,
    CurrencyEnum,
    TransactionItemPerformanceType,
    BestExecutionTypeDescriptions,
    TransactionStatusDescriptions,
    TransactionItemPerformanceTypeDescriptions,
    TransactionItemTypeDescriptions
} from "../types.generated";

import { AttachmentTransactionForm } from "./AttachmentTransactionForm";

const today = new Date().toISOString().slice(0, 10);

function randomExternalId(length: number): string {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const bytes = new Uint8Array(length);

    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }

    let out = "";
    for (let i = 0; i < bytes.length; i++) {
        out += alphabet[bytes[i] % alphabet.length];
    }
    return out;
}

export const defaultTransactionItem: CreateTransactionItemInput = {
    instrumentId: emptyObjectId,
    type: TransactionItemType.Fee,
    amount: 0.0,
    currency: CurrencyEnum.SEK,
    valueDate: today,
    quantity: 0.0,
    price: 0.0,
    fxRate: 0,
    externalAccountId: emptyObjectId,
    accountId: emptyObjectId
};
interface FormData {
    _id?: string;
    typename?: string;
    number?: number;
    carryOwnCostDate?: string;
    clientId?: string;
    brokerId?: string;
    clearingHouseId?: string;
    externalId?: string;
    source?: SourceType;
    parentId?: string;
    bestExecutionType?: BestExecutionType;
    brokerTradeId?: string;
    description?: string;
    createTimestamp?: string;
    error?: string;
    status?: TransactionStatus;
    tradeTimestamp?: string;
    tradeDate?: string;
    type?: TransactionType;
    uniqueTradeId?: string;
    updateTimestamp?: string;
    items?: TransactionItem[];
    updateUserInfo?: any;
}

const GET_DATA = gql`
    query {
        clients: parties(filter: { typeIn: [Client] }) {
            name
            _id
            instruments {
                _id
                name
                longName
                currency
            }
            accounts {
                _id
                name
                description
            }
            externalAccounts {
                _id
                name
                description
            }
        }

        brokers: parties(filter: { typeIn: [Broker] }) {
            _id
            name
            legalEntityIdentifier
        }

        clearinghouses: parties(filter: { typeIn: [ClearingHouse] }) {
            _id
            name
            legalEntityIdentifier
        }

        instruments(filter: { typeIn: [Instrument] }) {
            _id
            name
            longName
            currency
        }
    }
`;

export const TransactionPage = (): React.ReactElement => {
    const { id } = useParams<{ id: string }>();
    const previousId = usePrevious(id);
    const [, navigate] = useLocation();

    let transactionId = "000000000000000000000000";
    if (id && id !== "new") {
        transactionId = id;
    }

    const [showVersions, setShowVersions] = useState(false);

    const [{ fetching: transactionLoading, error: transactionError, data: transactionData }, refetch] = useQuery({
        query: GET_TRANSACTION,
        variables: { _id: transactionId, includeVersions: showVersions },
        requestPolicy: "network-only"
    });

    const [{ fetching: loading, error: getDataError, data }] = useQuery({ query: GET_DATA });

    const [formData, setFormData] = useState(null);

    const [_, createMutation] = useMutation(CREATE_TRANSACTION);
    const [__, updateMutation] = useMutation(UPDATE_TRANSACTIONS);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const [modal, setModal] = useState({ showModal: false, payload: null });
    const [cloning, setCloning] = useState(false);

    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    useEffect(() => {
        if (previousId !== id) {
            setFormData(null);
            setShowVersions(false);
        } else if (transactionData) {
            if (id === "new") {
                if (formData !== null) return;
                // default
                const today = new Date().toISOString(); //
                const initFormData: FormData = {
                    _id: emptyObjectId,
                    bestExecutionType: BestExecutionType.Portfolio,
                    brokerId: null,
                    brokerTradeId: "",
                    carryOwnCostDate: null,
                    clientId: "000000000000000000000000",
                    clearingHouseId: "000000000000000000000000",
                    createTimestamp: today,
                    description: "",
                    error: "",
                    externalId: "",
                    items: [],
                    number: 0,
                    source: SourceType.Internal,
                    status: TransactionStatus.Pending,
                    tradeTimestamp: today,
                    type: TransactionType.BondTrade,
                    updateTimestamp: today,
                    uniqueTradeId: ""
                };
                setFormData(initFormData);
                return;
            }

            if (!transactionData.transaction) return;

            // Important: urql can keep the previous query's `data` while the new variables are fetching.
            // If we only initialize when `formData === null`, we may accidentally "lock in" the previous transaction.
            if (formData !== null && formData._id === transactionData.transaction._id) return;

            const transaction: Transaction = cloneDeep(transactionData.transaction);
            const nextFormData: FormData = {
                _id: transaction._id,
                bestExecutionType: transaction.bestExecutionType,
                brokerId: transaction.brokerId,
                clearingHouseId: transaction.clearingHouseId || "000000000000000000000000",
                brokerTradeId: transaction.brokerTradeId,
                carryOwnCostDate: transaction.carryOwnCostDate,
                clientId: transaction.clientId,
                createTimestamp: transaction.createTimestamp,
                description: transaction.description,
                error: transaction.error,
                externalId: transaction.externalId,
                items: transaction.items as unknown as TransactionItem[],
                number: transaction.number,
                source: transaction.source,
                status: transaction.status,
                tradeTimestamp: transaction.tradeTimestamp,
                type: transaction.type,
                updateTimestamp: transaction.updateTimestamp,
                updateUserInfo: transaction.updateUserInfo,
                uniqueTradeId: transaction.uniqueTradeId || ""
            }
            setFormData(nextFormData);
        }
    }, [previousId, id, transactionData, formData, transactionId]);

    if (loading || (transactionLoading && !transactionData)) return <div>Loading</div>;

    if (transactionError) return <div>{"Error: " + transactionError.message}</div>;
    if (getDataError) return <div>{"Error: " + getDataError.message}</div>;

    if (!formData) return <div>Transaction with id '{id}' does not exist!</div>;

    const isCreateMode = id === "new";
    const versions = (transactionData && transactionData.transaction && transactionData.transaction.versions) || [];
    let transactionWithoutVersions = transactionData && transactionData.transaction;
    if (transactionWithoutVersions) {
        transactionWithoutVersions = { ...transactionWithoutVersions };
        delete transactionWithoutVersions.versions;
    }

    const clients = sortBy(data.clients, "name");
    const brokers = sortBy(data.brokers, "name");
    const clearinghouses = sortBy(data.clearinghouses, "name");
    const instruments = cloneDeep(data.instruments);
    brokers.unshift({ _id: "000000000000000000000000", name: "None" });
    clearinghouses.unshift({ _id: "000000000000000000000000", name: "None" });
    let accounts;
    let externalAccounts;
    let partyInstruments;
    let partyInstrumentsById: Record<any, any> = {};

    const clientsById = keyBy(clients, "_id");
    const client = clientsById[formData.clientId];
    const accountNone = { description: "None", name: "None", __typename: "PartyAccount", _id: "000000000000000000000000" };
    const externalAccountNone = { description: "None", name: "None", __typename: "PartyAccount", _id: "000000000000000000000000" };

    if (!client) {
        accounts = [accountNone];
        externalAccounts = [externalAccountNone];
    } else {
        accounts = cloneDeep(client.accounts);
        externalAccounts = cloneDeep(client.externalAccounts);
        partyInstruments = cloneDeep(client.instruments);
        partyInstrumentsById = keyBy(partyInstruments, "_id");
        instruments.push(...partyInstruments);
        accounts.push(accountNone);
        externalAccounts.push(externalAccountNone);
    }

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete transaction with id " + modal.payload.input._id + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        let mutationData: any;
                        updateMutation({ input: [modal.payload.input] })
                            .then((result) => {
                                mutationData = result.data.transactions[0];
                                setAlert({
                                    color: "success",
                                    visible: true,
                                    message: `The transaction '${mutationData._id}' was updated and deleted successfully!`
                                });
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            })
                            .finally(() => {
                                if (mutationData && mutationData._id) {
                                    setFormData(mutationData);
                                }
                            });
                    }}
                />
            ) : null}

            <div className="mx-auto px-4">
                <h1>Transaction</h1>
                <div className="flex flex-wrap -mx-2">
                    <Formik
                        key={transactionId}
                        enableReinitialize={true}
                        validateOnMount={true}
                        initialValues={formData}
                        validate={(validateFormData) => {
                            const errors: any = {};
                            if (validateFormData.clientId === "000000000000000000000000") {
                                errors.clientId = "No client selected";
                            }
                            if (validateFormData.externalId === "" || !validateFormData.externalId) {
                                errors.externalId = "ExternalId is required";
                            }
                            if (validateFormData.items.length === 0) {
                                errors.items = "Transaction needs to have at least one Transaction Item";
                            }
                            if (validateFormData.tradeTimestamp.length === 0 || !validateFormData.tradeTimestamp) {
                                errors.tradeTimeStamp = "Trade timstamp is required";
                            }
                            if (validateFormData.type === "" || !validateFormData.type) {
                                errors.type = "Transaction Type is required";
                            }
                            if (validateFormData.bestExecutionType === "" || !validateFormData.bestExecutionType) {
                                errors.bestExecutionType = "Best Execution Type is required";
                            }
                            if (validateFormData.status === "" || !validateFormData.status) {
                                errors.bestExecutionType = "Transaction status is required";
                            }

                            return Object.keys(errors).length > 0 ? errors : {};
                        }}
                        onSubmit={async (values, { setSubmitting, setErrors }) => {
                            const submitValues: FormData = values;

                            const validatedItems: TransactionItem[] = [];
                            if (submitValues.items && submitValues.items.length) {
                                for (const item of submitValues.items) {
                                    const validatedItem = cloneDeep(item);
                                    // Set by the server from the transaction, not allowed in UpdateTransactionItemInput
                                    delete validatedItem.clientId;
                                    delete validatedItem.transactionId;
                                    delete validatedItem.transactionStatus;
                                    delete validatedItem.transactionTradeDate;
                                    delete validatedItem.transactionTradeTimestamp;
                                    delete validatedItem.transactionType;
                                    delete validatedItem.performanceType;
                                    delete validatedItem.portfolioInstrumentId;
                                    validatedItems.push(validatedItem);
                                }
                            }

                            let input: Transaction = {
                                _id: submitValues._id,
                                __typename: "Transaction",
                                bestExecutionType: submitValues.bestExecutionType,
                                brokerId: submitValues.brokerId,
                                clearingHouseId: submitValues.clearingHouseId,
                                brokerTradeId: submitValues.brokerTradeId,
                                carryOwnCostDate: submitValues.carryOwnCostDate,
                                clientId: submitValues.clientId,
                                createTimestamp: submitValues.createTimestamp,
                                description: submitValues.description,
                                error: submitValues.error,
                                externalId: submitValues.externalId,
                                items: validatedItems,
                                number: submitValues.number,
                                status: submitValues.status,
                                tradeTimestamp: submitValues.tradeTimestamp,
                                tradeDate: submitValues.tradeDate,
                                type: submitValues.type,
                                uniqueTradeId: submitValues.uniqueTradeId,
                                source: submitValues.source,
                                parentId: submitValues.parentId,
                                updateUserInfo: submitValues.updateUserInfo

                                //issuerId: submitValues.issuerId,
                            };

                            input = recursivelyRemoveKey(input, "__typename");
                            //input = removeKey(input, "_id");
                            input = recursivelyRemoveKey(input, "createTimestamp");
                            input = recursivelyRemoveKey(input, "updateTimestamp");
                            input = recursivelyRemoveKey(input, "source");
                            input = recursivelyRemoveKey(input, "updateUserInfo");

                            let mutationData: FormData = null;
                            //delete input.attachments;
                            //let mutationData: Transaction = null;

                            if (isCreateMode) {
                                input = recursivelyRemoveKey(input, "_id");
                                await createMutation({ input })
                                    .then((result) => {
                                        if ("error" in result && result.error) {
                                            const message = formikUrqlErrorFormater(result.error, setErrors);
                                            setAlert({ color: "danger", visible: true, message });
                                        } else {
                                            mutationData = result.data.transaction;
                                            setFormData(null);
                                        }
                                        return true;
                                    })
                                    .catch((error) => {
                                        setAlert({ color: "danger", visible: true, message: error.toString() });
                                    })
                                    .finally(() => {
                                        setSubmitting(false);
                                        navigate("/transaction/" + mutationData._id, { replace: true });
                                    });
                            } else {
                                if (input.status && input.status === "Deleted") {
                                    setModal({ showModal: true, payload: { input: input } });
                                } else {
                                    await updateMutation({ input: [input] })
                                        .then((result) => {
                                            if ("error" in result && result.error) {
                                                const message = formikUrqlErrorFormater(result.error, setErrors);
                                                setAlert({ color: "danger", visible: true, message });
                                            } else {
                                                mutationData = result.data.transactions[0];
                                                setAlert({
                                                    color: "success",
                                                    visible: true,
                                                    message: `The transaction '${mutationData._id}' updated successfully!`
                                                });
                                            }
                                            return true;
                                        })
                                        .catch((error) => {
                                            setAlert({ color: "danger", visible: true, message: error.toString() });
                                        })
                                        .finally(() => {
                                            setSubmitting(false);
                                            refetch();
                                            if (mutationData && mutationData._id) {
                                                setFormData(mutationData);
                                            }
                                        });
                                }
                            }
                        }}
                    >
                        {({ isSubmitting, values }) => (
                            <Fragment>
                                <div className="w-full px-2 mb-4">
                                    <Form autoComplete="off">
                                        {alert.visible ? (
                                            <Alert style={{ marginTop: "10px" }} variant={alert.color} onClose={onDismissAlert} dismissible>
                                                {alert.message}
                                            </Alert>
                                        ) : null}
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-8/12 px-2 mb-4">
                                                <div className="flex flex-wrap -mx-2">
                                                    <TextField
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        name="_id"
                                                        label="Id"
                                                        disabled={true}
                                                    />
                                                    <SelectField
                                                        name="bestExecutionType"
                                                        label="Best execution type*"
                                                        options={Object.values(BestExecutionType)}
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                        tooltips={BestExecutionTypeDescriptions}
                                                    />
                                                    <SelectField
                                                        name="clientId"
                                                        label={<Link to={"/parties/" + values.clientId}>Client*</Link>}
                                                        options={clients.map((d) => ({ key: d._id, value: d.name }))}
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <SelectField
                                                        name="type"
                                                        label="Type*"
                                                        options={Object.values(TransactionType)}
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                    <SelectField
                                                        name="status"
                                                        label="Status*"
                                                        options={Object.values(TransactionStatus)}
                                                        tooltips={TransactionStatusDescriptions}
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                    <SelectField
                                                        name="source"
                                                        options={Object.keys(SourceType).map((item) => {
                                                            return { key: item, value: item };
                                                        })}
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <NumberField
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        name="number"
                                                        label="Number"
                                                        disabled={isSubmitting}
                                                    />
                                                    <TimestampField
                                                        name="tradeTimestamp"
                                                        label="Trade timestamp*"
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                    <SelectField
                                                        name="clearingHouseId"
                                                        label={<Link to={"/parties/" + values.clearingHouseId}>Clearing house</Link>}
                                                        options={clearinghouses.map((d) => ({ key: d._id, value: d.name }))}
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <SelectField
                                                        name="brokerId"
                                                        label={<Link to={"/parties/" + values.brokerId}>Broker</Link>}
                                                        options={brokers.map((d) => ({ key: d._id, value: d.name }))}
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                    <TextField
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        name="brokerTradeId"
                                                        label="Broker trade id"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <TextField
                                                        className="w-full px-2 mb-4"
                                                        name="error"
                                                        label="Error"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <NullableDateField
                                                        name="carryOwnCostDate"
                                                        label="Carry own cost date"
                                                        className="w-full md:w-8/12 px-2 mb-4"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <TextField
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        name="createTimestamp"
                                                        label="Create timestamp"
                                                        disabled={true}
                                                    />
                                                    <TextField
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        name="updateTimestamp"
                                                        label="Update timestamp"
                                                        disabled={true}
                                                    />
                                                    <TextField
                                                        className="w-full md:w-4/12 px-2 mb-4"
                                                        name="updateUserInfo.name"
                                                        label="Updated by"
                                                        disabled={true}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <TextField
                                                        className="w-full px-2 mb-4"
                                                        name="externalId"
                                                        label="External id*"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <TextField
                                                        className="w-full px-2 mb-4"
                                                        name="uniqueTradeId"
                                                        label="Unique trade id"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <TextField
                                                        className="w-full px-2 mb-4"
                                                        name="description"
                                                        label="Description"
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                                <h3>Transaction Items</h3>
                                                <div className="flex flex-wrap -mx-2">
                                                    <FieldArray
                                                        name="items"
                                                        render={(arrayHelpers) => (
                                                            <Fragment>
                                                                {values.items && values.items.length > 0 ? (
                                                                    values.items.map((item, index) => (
                                                                        <div key={index}>
                                                                            <div className="w-full px-2 mb-4">
                                                                                <TextField
                                                                                    className=""
                                                                                    name={`items[${index}]._id`}
                                                                                    label="Id"
                                                                                    disabled={true}
                                                                                />
                                                                                <TextField
                                                                                    className=""
                                                                                    name={`items[${index}].transactionId`}
                                                                                    label="Transaction id"
                                                                                    disabled={true}
                                                                                />
                                                                                <SelectField
                                                                                    className=""
                                                                                    name={`items[${index}].performanceType`}
                                                                                    label="Performance type"
                                                                                    options={Object.values(
                                                                                        TransactionItemPerformanceType
                                                                                    ).sort()}
                                                                                    tooltips={TransactionItemPerformanceTypeDescriptions}
                                                                                    disabled={true}
                                                                                />
                                                                                <SearchListField
                                                                                    className=""
                                                                                    name={`items[${index}].portfolioInstrumentId`}
                                                                                    label={
                                                                                        item.instrumentId &&
                                                                                        item.portfolioInstrumentId !== emptyObjectId ? (
                                                                                            <Link
                                                                                                to={
                                                                                                    "/instruments/" +
                                                                                                    item.portfolioInstrumentId
                                                                                                }
                                                                                            >
                                                                                                Portfolio instrument
                                                                                            </Link>
                                                                                        ) : (
                                                                                            "Portfolio instrument"
                                                                                        )
                                                                                    }
                                                                                    options={instruments.concat({
                                                                                        name: "None",
                                                                                        _id: emptyObjectId
                                                                                    })}
                                                                                    disabled={true}
                                                                                />
                                                                                <SearchListField
                                                                                    className=""
                                                                                    name={`items[${index}].instrumentId`}
                                                                                    label={
                                                                                        item.instrumentId in partyInstrumentsById ? (
                                                                                            <Link
                                                                                                to={
                                                                                                    "/parties/" +
                                                                                                    values.clientId +
                                                                                                    "/instruments/" +
                                                                                                    item.instrumentId
                                                                                                }
                                                                                            >
                                                                                                Party instrument
                                                                                            </Link>
                                                                                        ) : (
                                                                                            <Link to={"/instruments/" + item.instrumentId}>
                                                                                                Instrument
                                                                                            </Link>
                                                                                        )
                                                                                    }
                                                                                    options={instruments}
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                                <SelectField
                                                                                    className=""
                                                                                    name={`items[${index}].type`}
                                                                                    label="Type"
                                                                                    options={Object.values(TransactionItemType).sort()}
                                                                                    tooltips={TransactionItemTypeDescriptions}
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                                <SelectField
                                                                                    className=""
                                                                                    name={`items[${index}].currency`}
                                                                                    label="Currency"
                                                                                    options={Object.values(CurrencyEnum).sort()}
                                                                                    disabled={isSubmitting}
                                                                                />

                                                                                <DateField
                                                                                    name={`items[${index}].valueDate`}
                                                                                    label="Value date"
                                                                                    className=""
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                                <NumberField
                                                                                    className=""
                                                                                    name={`items[${index}].amount`}
                                                                                    label="Amount"
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                                <NumberField
                                                                                    className=""
                                                                                    name={`items[${index}].price`}
                                                                                    label="Price"
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                                <NumberField
                                                                                    className=""
                                                                                    name={`items[${index}].quantity`}
                                                                                    label="Quantity"
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                                <div title="If fxRate is 0, a scheduled job will set it correctly">
                                                                                    <NumberField
                                                                                        className=""
                                                                                        name={`items[${index}].fxRate`}
                                                                                        label="Fx rate"
                                                                                        disabled={isSubmitting}
                                                                                    />
                                                                                </div>
                                                                                <SelectField
                                                                                    className=""
                                                                                    name={`items[${index}].accountId`}
                                                                                    label={
                                                                                        <Link
                                                                                            to={
                                                                                                "/parties/" +
                                                                                                values.clientId +
                                                                                                "/accounts/" +
                                                                                                item.accountId
                                                                                            }
                                                                                        >
                                                                                            Account
                                                                                        </Link>
                                                                                    }
                                                                                    options={accounts.map((d) => ({
                                                                                        key: d._id,
                                                                                        value: d.name
                                                                                    }))}
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                                <SelectField
                                                                                    className=""
                                                                                    name={`items[${index}].externalAccountId`}
                                                                                    label={
                                                                                        <Link
                                                                                            to={
                                                                                                "/parties/" +
                                                                                                values.clientId +
                                                                                                "/externalAccounts/" +
                                                                                                item.externalAccountId
                                                                                            }
                                                                                        >
                                                                                            External account
                                                                                        </Link>
                                                                                    }
                                                                                    options={externalAccounts.map((d) => ({
                                                                                        key: d._id,
                                                                                        value: d.name
                                                                                    }))}
                                                                                    disabled={isSubmitting}
                                                                                />
                                                                            </div>
                                                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                                                <Button
                                                                                    variant="danger"
                                                                                    size="sm"
                                                                                    className="mr-1"
                                                                                    type="button"
                                                                                    onClick={() => arrayHelpers.remove(index)}
                                                                                >
                                                                                    -
                                                                                </Button>
                                                                                <Button
                                                                                    variant="primary"
                                                                                    size="sm"
                                                                                    type="button"
                                                                                    onClick={() =>
                                                                                        arrayHelpers.insert(
                                                                                            index,
                                                                                            cloneDeep(defaultTransactionItem)
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    +
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="w-full md:w-4/12 px-2 mb-4">
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            type="button"
                                                                            onClick={() =>
                                                                                arrayHelpers.push(cloneDeep(defaultTransactionItem))
                                                                            }
                                                                        >
                                                                            {/* show this when user has removed all items */}
                                                                            Add a transaction item
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </Fragment>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-full md:w-4/12 px-2 mb-4">
                                                <h2>Graphql format</h2>
                                                <pre>{stableStringify(transactionWithoutVersions, { space: "  " })}</pre>
                                                {!isCreateMode ? (
                                                    <div className="mt-3">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            type="button"
                                                            onClick={() => setShowVersions(!showVersions)}
                                                        >
                                                            {showVersions ? "Hide versions" : "Show versions"}
                                                        </Button>
                                                        {showVersions ? (
                                                            <div className="mt-2">
                                                                <h3>Previous versions</h3>
                                                                {transactionLoading ? (
                                                                    <div>Loading</div>
                                                                ) : (
                                                                    <pre>{stableStringify(versions, { space: "  " })}</pre>
                                                                )}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <SubmitButton disabled={isSubmitting || cloning} label={isCreateMode ? "Create" : "Update"} />
                                            {!isCreateMode ? (
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    type="button"
                                                    disabled={isSubmitting || cloning}
                                                    onClick={async () => {
                                                        setCloning(true);
                                                        try {
                                                            const clonedItems: CreateTransactionItemInput[] = (values.items || []).map(
                                                                (item: any): CreateTransactionItemInput => ({
                                                                    instrumentId: item.instrumentId,
                                                                    type: item.type,
                                                                    amount: item.amount,
                                                                    currency: item.currency,
                                                                    valueDate: item.valueDate,
                                                                    quantity: item.quantity,
                                                                    price: item.price,
                                                                    fxRate: item.fxRate,
                                                                    externalAccountId: item.externalAccountId,
                                                                    accountId: item.accountId
                                                                })
                                                            );

                                                            const input = recursivelyRemoveKey(
                                                                {
                                                                    bestExecutionType: values.bestExecutionType,
                                                                    brokerId: values.brokerId,
                                                                    clearingHouseId: values.clearingHouseId,
                                                                    brokerTradeId: values.brokerTradeId,
                                                                    carryOwnCostDate: values.carryOwnCostDate,
                                                                    clientId: values.clientId,
                                                                    description: values.description,
                                                                    externalId: randomExternalId(15),
                                                                    items: clonedItems,
                                                                    number: values.number,
                                                                    status: values.status,
                                                                    tradeTimestamp: values.tradeTimestamp,
                                                                    type: values.type,
                                                                    uniqueTradeId: values.uniqueTradeId
                                                                },
                                                                "__typename"
                                                            );

                                                            const result = await createMutation({ input });
                                                            if ("error" in result && result.error) {
                                                                setAlert({
                                                                    color: "danger",
                                                                    visible: true,
                                                                    message: formikUrqlErrorFormater(result.error, () => null as any)
                                                                });
                                                                return;
                                                            }

                                                            const newId = result?.data?.transaction?._id;
                                                            if (!newId) {
                                                                setAlert({
                                                                    color: "danger",
                                                                    visible: true,
                                                                    message: "Clone failed: createTransaction did not return an id."
                                                                });
                                                                return;
                                                            }

                                                            setFormData(null);
                                                            navigate("/transaction/" + newId, { replace: true });
                                                        } catch (error) {
                                                            setAlert({ color: "danger", visible: true, message: error.toString() });
                                                        } finally {
                                                            setCloning(false);
                                                        }
                                                    }}
                                                >
                                                    Clone
                                                </Button>
                                            ) : null}
                                        </div>
                                    </Form>
                                    {!isCreateMode ? (
                                        <div className="flex flex-wrap -mx-2 mt-3">
                                            <AttachmentTransactionForm transactionId={transactionId} />
                                        </div>
                                    ) : null}
                                </div>
                            </Fragment>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};
